// CurrentTaskPanel keeps all of the "work on this task" controls together.
// Breaking it out of the main screen keeps FocusModeV2 short and easier to follow.

import React from 'react';
import { format } from 'date-fns';
import {
  Clock,
  Calendar,
  Play,
  Square,
  ArrowRight,
  CalendarPlus,
  CheckCircle,
  ClipboardList,
  Edit,
  Save
} from 'lucide-react';
import { TaskWithContext, TimeEntry } from '../../lib/db';
import { formatMinutesShort } from './focusUtils';

interface CurrentTaskPanelProps {
  task: TaskWithContext | null;
  notes: string;
  onNotesChange: (value: string) => void;
  timeEntries: TimeEntry[];
  isTimerRunning: boolean;
  timerTaskId?: string | null;
  onStartTimer: () => void;
  onStopTimer: () => void;
  onCompleteTask: () => void;
  onBumpTask: () => void;
  onDeferToTomorrow: () => void;
  completingTask: boolean;
  isEditingSOP: boolean;
  editedSOP: string;
  onStartEditingSOP: () => void;
  onUpdateSOP: (value: string) => void;
  onCancelEditSOP: () => void;
  onSaveSOP: () => void;
}

export const CurrentTaskPanel: React.FC<CurrentTaskPanelProps> = ({
  task,
  notes,
  onNotesChange,
  timeEntries,
  isTimerRunning,
  timerTaskId,
  onStartTimer,
  onStopTimer,
  onCompleteTask,
  onBumpTask,
  onDeferToTomorrow,
  completingTask,
  isEditingSOP,
  editedSOP,
  onStartEditingSOP,
  onUpdateSOP,
  onCancelEditSOP,
  onSaveSOP
}) => {
  if (!task) {
    return (
      <div className="flex-1 bg-white rounded-lg shadow-sm p-6">
        <p className="text-gray-600">No tasks are ready for focus mode right now. Add a task or mark one live to get started.</p>
      </div>
    );
  }

  const timerIsForCurrentTask = isTimerRunning && timerTaskId === task.id;
  const disableStop = !notes.trim();
  const hasUsableEstimate = typeof task.time_estimate === 'number' && task.time_estimate > 0;
  const estimateDisplay = hasUsableEstimate ? formatMinutesShort(task.time_estimate!) : 'No estimate yet';
  const recentEntries = timeEntries.slice(0, 5);

  return (
    <div className="flex-1 bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Up now</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              task.queueCategory === 'overdue' ? 'bg-red-100 text-red-700' :
              task.queueCategory === 'regular' ? 'bg-purple-100 text-purple-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {task.queueCategory === 'overdue' ? 'Overdue Regular Task' :
                task.queueCategory === 'regular' ? 'Regular Task' : 'Deliverable Task'}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">{task.title}</h1>
          {task.goal && (
            <p className="text-sm text-gray-600 mt-1">{task.goal.title}</p>
          )}
        </div>
      </div>

      {/* SOP section keeps the how-to steps visible. */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <ClipboardList className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="font-medium text-blue-900">Standard Operating Procedure</h3>
          </div>
          {!isEditingSOP && (
            <button onClick={onStartEditingSOP} className="text-sm text-blue-600 hover:text-blue-700 flex items-center">
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </button>
          )}
        </div>
        {isEditingSOP ? (
          <div>
            <textarea
              value={editedSOP}
              onChange={(event) => onUpdateSOP(event.target.value)}
              className="w-full p-2 border rounded-md"
              rows={4}
              placeholder="Add step-by-step instructions..."
            />
            <div className="mt-2 flex gap-2">
              <button
                onClick={onSaveSOP}
                className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
              >
                <Save className="h-4 w-4 mr-1" />
                Save
              </button>
              <button
                onClick={onCancelEditSOP}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-600 whitespace-pre-wrap">
            {task.sop || (
              <button onClick={onStartEditingSOP} className="text-blue-600 hover:text-blue-700">
                Add Standard Operating Procedure
              </button>
            )}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{task.title}</h2>

          {task.client || task.project || task.matter || task.goal ? (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
              {task.client && <span className="font-medium">{task.client.name}</span>}
              {(task.project || task.matter) && task.client && <span className="text-gray-400">›</span>}
              {task.project && <span>{task.project.title}</span>}
              {task.matter && !task.project && <span>{task.matter.title}</span>}
              {task.goal && (task.client || task.project || task.matter) && <span className="text-gray-400">›</span>}
              {task.goal && <span>{task.goal.title}</span>}
            </div>
          ) : null}

          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              task.priority === 'High' ? 'bg-red-100 text-red-700' :
              task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
              'bg-green-100 text-green-700'
            }`}>
              {task.priority} Priority
            </span>
            {task.is_regular && (
              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                Regular Task
              </span>
            )}
            <span className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              Est. {estimateDisplay}
            </span>
            {task.deadline && (
              <span className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                Due {format(new Date(task.deadline), 'MMM d, yyyy')}
              </span>
            )}
            {/* Show a badge so non-technical users know the task will come back next day automatically. */}
            {task.notes?.includes('[Deferred to Tomorrow]') && (
              <span
                className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium"
                title="We bumped this task, so it auto-loads back into Focus Mode tomorrow"
              >
                Bumped · Returns Tomorrow
              </span>
            )}
          </div>
        </div>

        {task.description && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">Description</h3>
            <p className="text-gray-600">{task.description}</p>
          </div>
        )}

        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-1">Notes</h3>
          <textarea
            value={notes}
            onChange={(event) => onNotesChange(event.target.value)}
            placeholder="Add notes about this task..."
            className="w-full p-3 border rounded-md"
            rows={4}
          />
        </div>

        {recentEntries.length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            {/* Show prior work sessions so the user can quickly recall what was done last time */}
            <h3 className="text-sm font-medium text-gray-700 mb-2">Recent work sessions</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {recentEntries.map(entry => {
                const start = new Date(entry.start_time);
                const end = entry.end_time ? new Date(entry.end_time) : null;
                const minutes = end ? Math.max(1, Math.ceil((end.getTime() - start.getTime()) / 60000)) : null;
                return (
                  <div key={entry.id} className="text-sm text-gray-700">
                    <div className="font-medium text-gray-900">
                      {format(start, 'MMM d, h:mma')}
                      {minutes !== null ? ` · ${minutes} min` : ''}
                    </div>
                    <div className="text-gray-600">
                      {entry.notes ? entry.notes : <span className="italic text-gray-500">No notes recorded</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex gap-3">
            <button
              onClick={onStartTimer}
              disabled={isTimerRunning}
              className={`flex-1 px-4 py-3 rounded-md flex items-center justify-center ${
                isTimerRunning ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <Play className="h-5 w-5 mr-2" />
              {timerIsForCurrentTask ? 'Timer Running' : 'Start Timer'}
            </button>
            {timerIsForCurrentTask && (
              <button
                onClick={onStopTimer}
                disabled={disableStop}
                className={`px-4 py-3 rounded-md flex items-center ${
                  disableStop ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'
                }`}
                title={disableStop ? 'Add notes before stopping timer' : 'Stop timer and save time entry'}
              >
                <Square className="h-5 w-5 mr-2" />
                Stop Timer
              </button>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={onCompleteTask}
              disabled={completingTask}
              className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 flex items-center justify-center"
            >
              <CheckCircle className="h-5 w-5 mr-2" />
              Complete Task
            </button>
            <div className="flex gap-2">
              <button
                onClick={onBumpTask}
                className="px-4 py-3 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 flex items-center"
              >
                <ArrowRight className="h-5 w-5 mr-2" />
                Bump
              </button>
              <button
                onClick={onDeferToTomorrow}
                className="px-4 py-3 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 flex items-center"
              >
                <CalendarPlus className="h-5 w-5 mr-2" />
                Tomorrow
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentTaskPanel;
